import {
  AvailabilitiesModel,
  FormEntityType,
  NetworkingModel,
  ParticipantEmailOutboxStatus,
  ParticipantGroupType,
  ParticipantImportMode,
  ParticipantImportStatus,
  ParticipantType,
  Prisma,
  TriggerV2Type,
  UserRoleAssignmentEntityType,
  prisma,
} from "client";
import { deleteExhibitor } from "../exhibitor";
import { deleteSponsor } from "../sponsor";
import {
  buildQrReferenceAvailabilityFilter,
  findDuplicateParticipantImportEmails,
  findDuplicateQrReferenceNumbers,
  findDuplicateNormalizedValues,
  getParticipantImportMaxRows,
  handlerLeaseAllowsRollback,
  isBlockingParticipantImport,
  isLiveParticipantImportQueueState,
  isParticipantImportObjectId,
  isParticipantImportReadyToComplete,
  normalizeParticipantImportEmail,
  normalizeQrReferenceNumber,
  participantImportCreatorIds,
  shouldAbandonPendingParticipantImport,
  shouldRecoverDeadParticipantImport,
} from "./import.pure";

export {
  buildQrReferenceAvailabilityFilter,
  findDuplicateParticipantImportEmails,
  findDuplicateQrReferenceNumbers,
  findDuplicateNormalizedValues,
  getParticipantImportMaxRows,
  handlerLeaseAllowsRollback,
  isBlockingParticipantImport,
  isLiveParticipantImportQueueState,
  isParticipantImportObjectId,
  isParticipantImportReadyToComplete,
  normalizeParticipantImportEmail,
  normalizeQrReferenceNumber,
  participantImportCreatorIds,
  shouldAbandonPendingParticipantImport,
  shouldRecoverDeadParticipantImport,
};

export type ParticipantImportRow = {
  row: number;
  email: string;
  name: string;
  qrReferenceNumber?: string | null;
};

export type PreflightParticipantImportInput = {
  eventId: string;
  whiteLabelId: string;
  participantGroupId: string;
  participantGroupType: ParticipantGroupType;
  mode: ParticipantImportMode;
  entityId?: string;
  rows: ParticipantImportRow[];
};

export type ParticipantImportPreflightResult = {
  row: number;
  errors: string[];
};

export type CreateParticipantImportInput = {
  mode: ParticipantImportMode;
  eventId: string;
  participantGroupId: string;
  entityId?: string;
  participantGroupType: ParticipantGroupType;
  createdById: string;
  whiteLabelId: string;
  totalRows: number;
  /** Opt-in. Omitted means no creation/profile/company emails are sent. */
  sendCreationEmails?: boolean;
};

export type CreateParticipantEmailOutboxInput = {
  importId: string;
  eventId: string;
  whiteLabelId: string;
  recipientEmail: string;
  trigger: TriggerV2Type;
  payload: Prisma.InputJsonValue;
  idempotencyKey: string;
  queueJobId?: string;
};

const normalizeName = (name: string) => name.trim().toLowerCase();

export const QR_REFERENCE_CONFLICT_MESSAGE =
  "Ref number (QR) is already assigned to another participant in this event";

export class QrReferenceConflictError extends Error {
  constructor() {
    super(QR_REFERENCE_CONFLICT_MESSAGE);
    this.name = "QrReferenceConflictError";
  }
}

export function isQrReferenceUniqueConstraintError(error: unknown): boolean {
  if (
    error instanceof Prisma.PrismaClientKnownRequestError &&
    error.code === "P2002" &&
    `${JSON.stringify(error.meta ?? "")} ${error.message}`.includes(
      "qrReferenceNumber"
    )
  ) {
    return true;
  }
  return (
    error instanceof Error &&
    error.message.includes("qrReferenceNumber") &&
    /duplicate key|unique constraint/i.test(error.message)
  );
}

export async function assertQrReferenceNumberAvailable(
  eventId: string,
  value: string | null | undefined,
  excludeProfileId?: string,
  db: Prisma.TransactionClient | typeof prisma = prisma
): Promise<string | null> {
  const qrReferenceNumber = normalizeQrReferenceNumber(value);
  if (!qrReferenceNumber) return null;
  const where = buildQrReferenceAvailabilityFilter(
    eventId,
    qrReferenceNumber,
    excludeProfileId
  );
  if (!where) return null;

  // Include the partial-index predicate so this lookup uses the QR index
  // instead of scanning another eventId-prefixed index.
  const matches = (await db.profile.aggregateRaw({
    pipeline: [
      {
        $match: {
          $and: [
            { eventId: { $oid: eventId } },
            { qrReferenceNumber },
            { qrReferenceNumber: { $type: "string", $gt: "" } },
            ...(excludeProfileId
              ? [{ _id: { $ne: { $oid: excludeProfileId } } }]
              : []),
          ],
        },
      },
      { $limit: 1 },
      { $project: { _id: 1 } },
    ],
  })) as unknown as unknown[];
  if (matches.length > 0) throw new QrReferenceConflictError();

  return qrReferenceNumber;
}

export async function resolveParticipantImportCreator(
  session: {
    user?: { id?: string | null; email?: string | null };
    sub?: string | null;
  },
  whiteLabelId?: string
) {
  for (const id of participantImportCreatorIds(session)) {
    const user = await prisma.user.findUnique({ where: { id } });
    if (user) return user;
  }

  const email = session.user?.email
    ? normalizeParticipantImportEmail(session.user.email)
    : "";
  if (!email) return null;

  if (whiteLabelId) {
    const scoped = await prisma.user.findUnique({
      where: {
        email_accessibleWhiteLabelId: {
          email,
          accessibleWhiteLabelId: whiteLabelId,
        },
      },
    });
    if (scoped) return scoped;
  }

  return prisma.user.findFirst({ where: { email } });
}

export async function preflightParticipantImport(
  input: PreflightParticipantImportInput
): Promise<ParticipantImportPreflightResult[]> {
  if (input.mode === ParticipantImportMode.TEAM_MEMBERS && !input.entityId) {
    throw new Error("entityId is required for team-member imports");
  }

  const normalizedEmails = input.rows.map(({ email }) =>
    normalizeParticipantImportEmail(email)
  );
  const emails = [...new Set(normalizedEmails.filter(Boolean))];
  const duplicateEmails =
    findDuplicateParticipantImportEmails(normalizedEmails);
  const normalizedQrReferenceNumbers = input.rows.map(({ qrReferenceNumber }) =>
    normalizeQrReferenceNumber(qrReferenceNumber)
  );
  const qrReferenceNumbers = [
    ...new Set(normalizedQrReferenceNumbers.filter(Boolean)),
  ];
  const duplicateQrReferenceNumbers = findDuplicateQrReferenceNumbers(
    normalizedQrReferenceNumbers
  );
  const importCompanyNames =
    input.mode === ParticipantImportMode.PARTICIPANTS
      ? input.rows.map(({ name }) => name.trim()).filter(Boolean)
      : [];
  const companyNames = [...new Set(importCompanyNames)];
  const duplicateCompanyNames =
    input.mode === ParticipantImportMode.PARTICIPANTS &&
    (input.participantGroupType === ParticipantGroupType.EXHIBITORS ||
      input.participantGroupType === ParticipantGroupType.SPONSORS)
      ? findDuplicateNormalizedValues(importCompanyNames)
      : new Set<string>();

  const profilesPromise = prisma.profile.findMany({
    where: {
      eventId: input.eventId,
      OR: [{ email: { in: emails } }, { user: { email: { in: emails } } }],
    },
    select: { email: true, user: { select: { email: true } } },
  });
  const invitationsPromise = input.entityId
    ? prisma.participantInvitation.findMany({
        where: {
          entityId: input.entityId,
          email: { in: emails },
        },
        select: { email: true },
      })
    : Promise.resolve([]);
  const exhibitorsPromise =
    input.participantGroupType === ParticipantGroupType.EXHIBITORS
      ? prisma.exhibitor.findMany({
          where: {
            eventId: input.eventId,
            OR: [
              { email: { in: emails } },
              ...(companyNames.length
                ? [
                    {
                      translations: {
                        some: {
                          name: {
                            in: companyNames,
                            mode: "insensitive" as const,
                          },
                        },
                      },
                    },
                  ]
                : []),
            ],
          },
          select: { email: true, translations: { select: { name: true } } },
        })
      : Promise.resolve([]);
  const sponsorsPromise =
    input.participantGroupType === ParticipantGroupType.SPONSORS
      ? prisma.sponsor.findMany({
          where: {
            eventId: input.eventId,
            OR: [
              { email: { in: emails } },
              ...(companyNames.length
                ? [
                    {
                      translations: {
                        some: {
                          name: {
                            in: companyNames,
                            mode: "insensitive" as const,
                          },
                        },
                      },
                    },
                  ]
                : []),
            ],
          },
          select: { email: true, translations: { select: { name: true } } },
        })
      : Promise.resolve([]);
  const qrProfilesPromise: Promise<Array<{ qrReferenceNumber?: string }>> =
    qrReferenceNumbers.length
      ? prisma.profile
          .aggregateRaw({
            // Keep this aligned with the partial index used by single-row checks.
            pipeline: [
              {
                $match: {
                  $and: [
                    { eventId: { $oid: input.eventId } },
                    { qrReferenceNumber: { $in: qrReferenceNumbers } },
                    {
                      qrReferenceNumber: {
                        $type: "string",
                        $gt: "",
                      },
                    },
                  ],
                },
              },
              { $project: { _id: 0, qrReferenceNumber: 1 } },
            ],
          })
          .then(
            (rows) =>
              rows as unknown as Array<{ qrReferenceNumber?: string }>
          )
      : Promise.resolve([]);

  const [profiles, invitations, exhibitors, sponsors, qrProfiles] =
    await Promise.all([
      profilesPromise,
      invitationsPromise,
      exhibitorsPromise,
      sponsorsPromise,
      qrProfilesPromise,
    ]);
  const profileEmails = new Set(
    profiles.flatMap(({ email, user }) =>
      [email, user?.email].flatMap((value) =>
        value ? [normalizeParticipantImportEmail(value)] : []
      )
    )
  );
  const invitationEmails = new Set(
    invitations.map(({ email }) => normalizeParticipantImportEmail(email))
  );
  const companies = [...exhibitors, ...sponsors];
  const companyEmails = new Set(
    companies.flatMap(({ email }) =>
      email ? [normalizeParticipantImportEmail(email)] : []
    )
  );
  const existingCompanyNames = new Set(
    companies.flatMap(({ translations }) =>
      translations.map(({ name }) => normalizeName(name))
    )
  );
  const existingQrReferenceNumbers = new Set(
    qrProfiles.flatMap(({ qrReferenceNumber }) =>
      qrReferenceNumber ? [qrReferenceNumber] : []
    )
  );

  return input.rows.map(({ row, name }, index) => {
    const email = normalizedEmails[index];
    const qrReferenceNumber = normalizedQrReferenceNumbers[index];
    const errors: string[] = [];
    if (duplicateEmails.has(email))
      errors.push("Duplicate email in import file");
    if (profileEmails.has(email))
      errors.push("Email already has a profile in this event");
    if (companyEmails.has(email))
      errors.push("Email already belongs to a company in this event");
    if (invitationEmails.has(email))
      errors.push("Email already has an invitation for this company");
    if (
      input.mode === ParticipantImportMode.PARTICIPANTS &&
      existingCompanyNames.has(normalizeName(name))
    ) {
      errors.push(`Name "${name.trim()}" already exists in this event`);
    }
    if (duplicateCompanyNames.has(normalizeName(name))) {
      errors.push(`Duplicate Name "${name.trim()}" in the Name column`);
    }
    if (
      qrReferenceNumber &&
      duplicateQrReferenceNumbers.has(qrReferenceNumber)
    ) {
      errors.push(
        `Duplicate Ref number (QR) "${qrReferenceNumber}" in the import file`
      );
    }
    if (
      qrReferenceNumber &&
      existingQrReferenceNumbers.has(qrReferenceNumber)
    ) {
      errors.push(QR_REFERENCE_CONFLICT_MESSAGE);
    }
    return { row, errors };
  });
}

export function createParticipantImport(input: CreateParticipantImportInput) {
  return prisma.participantImport.create({
    data: {
      ...input,
      sendCreationEmails: input.sendCreationEmails === true,
      status: ParticipantImportStatus.PENDING,
      processedRows: 0,
    },
  });
}

const STARTABLE_PARTICIPANT_IMPORT_STATUSES = [
  ParticipantImportStatus.PENDING,
  ParticipantImportStatus.ROLLED_BACK,
] as const;

export async function markParticipantImportProcessing(importId: string) {
  const updated = await prisma.participantImport.updateMany({
    where: {
      id: importId,
      status: { in: [...STARTABLE_PARTICIPANT_IMPORT_STATUSES] },
    },
    data: {
      status: ParticipantImportStatus.PROCESSING,
      processedRows: 0,
      error: null,
      completedAt: null,
      rolledBackAt: null,
    },
  });
  return updated.count > 0;
}

export function updateParticipantImportProgress(
  importId: string,
  processedRows: number
) {
  if (!Number.isInteger(processedRows) || processedRows < 0)
    throw new Error("processedRows must be a non-negative integer");
  return prisma.participantImport.update({
    where: { id: importId },
    data: { processedRows },
  });
}

export function completeParticipantImport(
  importId: string,
  processedRows?: number
) {
  return prisma.participantImport.update({
    where: { id: importId },
    data: {
      status: ParticipantImportStatus.COMPLETED,
      completedAt: new Date(),
      ...(processedRows === undefined ? {} : { processedRows }),
    },
  });
}

const errorJson = (error: unknown): Prisma.InputJsonValue => ({
  message: error instanceof Error ? error.message : String(error),
});

export function markParticipantImportEnqueueFailed(
  importId: string,
  error: unknown
) {
  return prisma.participantImport.updateMany({
    where: { id: importId, status: ParticipantImportStatus.PENDING },
    data: {
      status: ParticipantImportStatus.ENQUEUE_FAILED,
      error: errorJson(error),
    },
  });
}

export const markPendingParticipantImportEnqueueFailed =
  markParticipantImportEnqueueFailed;

export function getParticipantImport(importId: string) {
  return prisma.participantImport.findUnique({ where: { id: importId } });
}

const ACTIVE_PARTICIPANT_IMPORT_STATUSES = [
  ParticipantImportStatus.PENDING,
  ParticipantImportStatus.PROCESSING,
  ParticipantImportStatus.ROLLING_BACK,
  ParticipantImportStatus.ROLLBACK_FAILED,
] as const;

export function listActiveParticipantImports(eventId: string) {
  return prisma.participantImport.findMany({
    where: {
      eventId,
      status: { in: [...ACTIVE_PARTICIPANT_IMPORT_STATUSES] },
    },
    select: {
      id: true,
      status: true,
      createdAt: true,
      updatedAt: true,
      processedRows: true,
      totalRows: true,
    },
  });
}

export function createParticipantEmailOutbox(
  input: CreateParticipantEmailOutboxInput
) {
  return prisma.participantEmailOutbox.upsert({
    where: { idempotencyKey: input.idempotencyKey },
    create: {
      ...input,
      recipientEmail: normalizeParticipantImportEmail(input.recipientEmail),
    },
    update: {},
  });
}

export function getPendingParticipantEmailOutbox(
  limit = 100,
  importId?: string
) {
  return prisma.participantEmailOutbox.findMany({
    where: {
      status: ParticipantEmailOutboxStatus.PENDING,
      import: { status: ParticipantImportStatus.COMPLETED },
      ...(importId ? { importId } : {}),
    },
    orderBy: { createdAt: "asc" },
    take: limit,
  });
}

export function markParticipantEmailOutboxDispatched(
  id: string,
  queueJobId: string
) {
  return prisma.participantEmailOutbox.update({
    where: { id },
    data: {
      status: ParticipantEmailOutboxStatus.DISPATCHED,
      queueJobId,
      dispatchedAt: new Date(),
      attempts: { increment: 1 },
      error: null,
    },
  });
}

export function recordParticipantEmailOutboxFailure(
  id: string,
  error: unknown
) {
  return prisma.participantEmailOutbox.update({
    where: { id },
    data: {
      attempts: { increment: 1 },
      error: errorJson(error),
    },
  });
}

async function assertImportedCompaniesAreSafe(
  importId: string,
  eventId: string,
  teamUserIds: string[]
) {
  if (!teamUserIds.length) return;
  const unsafeProfile = await prisma.profile.findFirst({
    where: {
      eventId,
      userId: { in: teamUserIds },
      OR: [
        { participantImportId: null },
        { participantImportId: { not: importId } },
        { orders: { some: {} } },
      ],
    },
    select: { id: true },
  });
  if (unsafeProfile) {
    throw new Error(
      "Imported company has a team profile that cannot be safely removed"
    );
  }
}

async function deleteImportFormTemplates(formTemplateIds: string[]) {
  const ids = [...new Set(formTemplateIds)];
  if (!ids.length) return;

  const formResults = await prisma.formResult.findMany({
    where: { formTemplateId: { in: ids } },
    select: { id: true },
  });
  const formResultIds = formResults.map(({ id }) => id);
  if (formResultIds.length) {
    await prisma.formResultEntity.deleteMany({
      where: { formResultId: { in: formResultIds } },
    });
    await prisma.formResult.deleteMany({
      where: { id: { in: formResultIds } },
    });
  }
  await prisma.formEntity.deleteMany({
    where: { formTemplateId: { in: ids } },
  });
  await prisma.formTemplateTranslations.deleteMany({
    where: { formTemplateId: { in: ids } },
  });
  await prisma.formTemplate.deleteMany({ where: { id: { in: ids } } });
}

export async function rollbackParticipantImport(importId: string) {
  const participantImport = await prisma.participantImport.findUnique({
    where: { id: importId },
  });
  if (!participantImport) throw new Error("Participant import not found");
  if (participantImport.status === ParticipantImportStatus.ROLLED_BACK)
    return participantImport;

  await prisma.participantImport.update({
    where: { id: importId },
    data: { status: ParticipantImportStatus.ROLLING_BACK },
  });

  try {
    const [exhibitors, sponsors, profiles, invitations, contactGroups, users] =
      await Promise.all([
        prisma.exhibitor.findMany({
          where: { participantImportId: importId },
          select: { id: true, teamIds: true },
        }),
        prisma.sponsor.findMany({
          where: { participantImportId: importId },
          select: { id: true, teamIds: true },
        }),
        prisma.profile.findMany({
          where: { participantImportId: importId },
          select: {
            id: true,
            userId: true,
            orders: { select: { id: true }, take: 1 },
          },
        }),
        prisma.participantInvitation.findMany({
          where: { participantImportId: importId },
          select: {
            id: true,
            entityId: true,
            entityType: true,
            userId: true,
          },
        }),
        prisma.contactBookGroup.findMany({
          where: { participantImportId: importId },
          select: { id: true },
        }),
        prisma.user.findMany({
          where: { participantImportId: importId },
          select: { id: true },
        }),
      ]);

    const unsafeTaggedProfile = profiles.find(({ orders }) => orders.length);
    if (unsafeTaggedProfile)
      throw new Error("Imported profile has orders and cannot be rolled back");

    const importedCompanyTeamIds = [
      ...exhibitors.flatMap(({ teamIds }) => teamIds),
      ...sponsors.flatMap(({ teamIds }) => teamIds),
    ];
    await assertImportedCompaniesAreSafe(
      importId,
      participantImport.eventId,
      importedCompanyTeamIds
    );

    const contactGroupIds = contactGroups.map(({ id }) => id);
    if (contactGroupIds.length) {
      const retainedContact = await prisma.profile.findFirst({
        where: {
          contactBookGroupId: { in: contactGroupIds },
          OR: [
            { participantImportId: null },
            { participantImportId: { not: importId } },
          ],
        },
        select: { id: true },
      });
      if (retainedContact)
        throw new Error(
          "Imported contact group contains a profile that must be retained"
        );
    }

    for (const exhibitor of exhibitors) await deleteExhibitor(exhibitor.id);
    for (const sponsor of sponsors) await deleteSponsor(sponsor.id);

    const importedExhibitorIds = new Set(exhibitors.map(({ id }) => id));
    const importedSponsorIds = new Set(sponsors.map(({ id }) => id));
    const taggedUserIds = users.map(({ id }) => id);
    const importedUserIds = [
      ...new Set([
        ...taggedUserIds,
        ...profiles.flatMap(({ userId }) => (userId ? [userId] : [])),
      ]),
    ];
    for (const invitation of invitations) {
      if (!invitation.userId) continue;
      const disconnect = [{ id: invitation.userId }];
      if (invitation.entityType === ParticipantType.EXHIBITOR) {
        if (importedExhibitorIds.has(invitation.entityId)) continue;
        const entityExists = await prisma.exhibitor.findUnique({
          where: { id: invitation.entityId },
          select: { id: true },
        });
        if (entityExists)
          await prisma.exhibitor.update({
            where: { id: invitation.entityId },
            data: { team: { disconnect } },
          });
        await prisma.assignmentModel.deleteMany({
          where: {
            userId: invitation.userId,
            entityId: invitation.entityId,
            entityType: UserRoleAssignmentEntityType.EXHIBITOR,
          },
        });
      } else if (invitation.entityType === ParticipantType.SPONSOR) {
        if (importedSponsorIds.has(invitation.entityId)) continue;
        const entityExists = await prisma.sponsor.findUnique({
          where: { id: invitation.entityId },
          select: { id: true },
        });
        if (entityExists)
          await prisma.sponsor.update({
            where: { id: invitation.entityId },
            data: { team: { disconnect } },
          });
        await prisma.assignmentModel.deleteMany({
          where: {
            userId: invitation.userId,
            entityId: invitation.entityId,
            entityType: UserRoleAssignmentEntityType.SPONSOR,
          },
        });
      }
    }

    await prisma.participantInvitation.deleteMany({
      where: { participantImportId: importId },
    });
    await prisma.participantEmailOutbox.deleteMany({
      where: {
        importId,
        status: ParticipantEmailOutboxStatus.PENDING,
      },
    });

    const remainingProfiles = await prisma.profile.findMany({
      where: { participantImportId: importId },
      select: { id: true },
    });
    const profileIds = remainingProfiles.map(({ id }) => id);
    for (const id of profileIds) {
      await prisma.profile.update({
        where: { id },
        data: { participantGroups: { set: [] } },
      });
    }
    if (profileIds.length) {
      await Promise.all([
        prisma.formEntity.deleteMany({
          where: { entityID: { in: profileIds } },
        }),
        prisma.formResultEntity.deleteMany({
          where: { profileId: { in: profileIds } },
        }),
        prisma.profileAgendaSession.deleteMany({
          where: { profileId: { in: profileIds } },
        }),
      ]);
      await prisma.profile.deleteMany({ where: { id: { in: profileIds } } });
    }

    if (contactGroupIds.length) {
      const contactGroupForms = await prisma.formEntity.findMany({
        where: {
          entityID: { in: contactGroupIds },
          type: FormEntityType.CONTACT_BOOK_GROUP,
          formTemplateId: { not: null },
        },
        select: { formTemplateId: true },
      });
      await deleteImportFormTemplates(
        contactGroupForms.flatMap(({ formTemplateId }) =>
          formTemplateId ? [formTemplateId] : []
        )
      );
      await prisma.contactBookGroup.deleteMany({
        where: { id: { in: contactGroupIds } },
      });
    }

    if (importedUserIds.length) {
      await Promise.all([
        prisma.networking.deleteMany({
          where: {
            eventId: participantImport.eventId,
            entityModel: NetworkingModel.USER,
            OR: [
              { entityId: { in: importedUserIds } },
              { createdById: { in: importedUserIds } },
            ],
          },
        }),
        prisma.availability.deleteMany({
          where: {
            eventId: participantImport.eventId,
            entityModel: AvailabilitiesModel.USER,
            OR: [
              { entityId: { in: importedUserIds } },
              { createdById: { in: importedUserIds } },
            ],
          },
        }),
      ]);
      const removableUsers = await prisma.user.findMany({
        where: {
          id: { in: taggedUserIds },
          profiles: { none: {} },
          exhibitors: { none: {} },
          sponsors: { none: {} },
        },
        select: { id: true },
      });
      if (removableUsers.length) {
        const removableUserIds = removableUsers.map(({ id }) => id);
        const formTemplates = await prisma.formTemplate.findMany({
          where: { createdById: { in: removableUserIds } },
          select: { id: true },
        });
        const formTemplateIds = formTemplates.map(({ id }) => id);
        await deleteImportFormTemplates(formTemplateIds);
        await prisma.assignmentModel.deleteMany({
          where: { userId: { in: removableUserIds } },
        });
        await prisma.user.deleteMany({
          where: { id: { in: removableUserIds } },
        });
      }
    }

    const [
      exhibitorCount,
      sponsorCount,
      profileCount,
      invitationCount,
      groupCount,
      importedUserCount,
    ] = await Promise.all([
      prisma.exhibitor.count({ where: { participantImportId: importId } }),
      prisma.sponsor.count({ where: { participantImportId: importId } }),
      prisma.profile.count({ where: { participantImportId: importId } }),
      prisma.participantInvitation.count({
        where: { participantImportId: importId },
      }),
      prisma.contactBookGroup.count({
        where: { participantImportId: importId },
      }),
      prisma.user.count({ where: { participantImportId: importId } }),
    ]);
    if (
      exhibitorCount ||
      sponsorCount ||
      profileCount ||
      invitationCount ||
      groupCount ||
      importedUserCount
    ) {
      throw new Error("Participant import rollback left tagged records");
    }

    return await prisma.participantImport.update({
      where: { id: importId },
      data: {
        status: ParticipantImportStatus.ROLLED_BACK,
        processedRows: 0,
        rolledBackAt: new Date(),
      },
    });
  } catch (error) {
    await prisma.participantImport.update({
      where: { id: importId },
      data: {
        status: ParticipantImportStatus.ROLLBACK_FAILED,
        error: errorJson(error),
      },
    });
    throw error;
  }
}
